import { Injectable } from '@angular/core';
import { SchemaField } from 'src/app/modules/core/store/models/document.model';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import { Layer } from 'src/app/modules/cop/store/cop/models';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { MapLocation, BboxGeometry, Point } from 'src/app/modules/cop/services/geometry.model';

const DEFAULT_GEOMETRY_FIELD = 'geometry';

@Injectable({ providedIn: 'root' })
export class UtilService {
  constructor(private distanceService: DistanceService) {
    this.defaultFormat = {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
  }
  private defaultFormat: { [key: string]: any };

  public static getDateNetFormat(date: string): number {
    const re = /-?\d+/;
    const m = re.exec(date);
    return parseInt(m[0], 10);
  }

  /**
   * Given the date field and format, return de expected date with the proper format
   * @param value date field value to parse
   * @param format final format expected
   */
  public parseDate(value: number, formatOptions: any = this.defaultFormat, adjustForTimeZone: boolean = false): string {
    const valueFloor = Math.floor(value);

    if (isNaN(valueFloor) || !valueFloor) {
      let newDate = new Date(value);
      if (adjustForTimeZone) {
        newDate = new Date(newDate.getTime() + new Date().getTimezoneOffset() * 60 * 1000);
      }
      return newDate.toLocaleString([], formatOptions);
    }

    let newDate = new Date(valueFloor.toString().length <= 10 ? valueFloor * 1000 : valueFloor);
    if (adjustForTimeZone) {
      const offset = new Date().getTimezoneOffset();
      newDate = new Date(newDate.getTime() + new Date().getTimezoneOffset() * 60 * 1000);
    }
    return newDate.toLocaleString([], formatOptions);
  }

  /**
   * Given the date field from data of the proxy, return date with proper format to show in a map
   * @param date date field value to parse
   */
  public ParseDateToGMTString(date: string): string {
    const currentYear = new Date().getUTCFullYear();
    const dates = new Date(date + ' ' + currentYear).toUTCString().split(',');
    const tempDates = dates[1].trim().split(' ');
    const hours = tempDates[3].split(':');
    return dates[0] + ' ' + tempDates[0] + ', ' + hours[0] + ':' + hours[1] + ' GMT';
  }

  public packFeaturesArrayAfterEditingIntoGeoJson(
    featuresToPack: GeoJSON.Feature[]
  ): GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry {
    if (!featuresToPack || featuresToPack.length === 0) {
      return null;
    }
    const featureCollection = {
      type: 'FeatureCollection',
      features: featuresToPack.map(f => {
        return {
          ...f,
          id: !f.properties?.id_before_edit ? undefined : f.properties?.id_before_edit,
          properties: {
            ...f.properties,
            id_before_edit: undefined
          }
        };
      })
    } as GeoJSON.FeatureCollection;

    if (featureCollection.features.length === 1) {
      if (!featureCollection.features[0].properties) return featureCollection.features[0].geometry;
    }

    return featureCollection;
  }

  public unpackFeaturesForEditingToArrayFromGeoJson(
    geometry: GeoJSON.FeatureCollection | GeoJSON.FeatureCollection | GeoJSON.Geometry
  ): GeoJSON.Feature[] {
    const features = [];
    const arrayStackToFlatten = [];
    arrayStackToFlatten.push(geometry);
    let currentFeatureEditId = 0;
    // find geometries inside collections
    while (arrayStackToFlatten.length > 0) {
      // Pop geometry
      const arr = arrayStackToFlatten.pop();
      if (!arr) {
        continue;
      }
      if (!arr.type) {
        if (Array.isArray(arr) && arr.length > 0) {
          arr.forEach(a => arrayStackToFlatten.push(a));
        }

        continue;
      }
      // Explore Geomtries, extracting core
      const featureType = arr.type.toLowerCase();
      if (featureType === 'featurecollection') {
        // unpack any features within the collection
        if (arr.features && arr.features.length > 0) {
          arr.features.forEach(element => {
            arrayStackToFlatten.unshift(element);
          });
        }
        continue;
      }
      // Wrap naked geometries in Features
      if (featureType !== 'feature') {
        arrayStackToFlatten.unshift({
          type: 'Feature',
          properties: null,
          geometry: arr
        } as GeoJSON.Feature);
        continue;
      }
      if (!arr.geometry?.type) {
        continue;
      }

      const geometryType = arr.geometry.type.toLowerCase();
      // extract a feature for each geometry in a geometry collection
      if (geometryType === 'geometrycollection') {
        const featureArray = arr.geometry.geometries.map(element => {
          const featureFromGeometry = {
            ...arr,
            geometry: element
          };
          return featureFromGeometry;
        }) as GeoJSON.Feature[];
        arrayStackToFlatten.push(featureArray.reverse());
        continue;
      }

      if (geometryType === 'multipoint') {
        if (arr.geometry.coordinates && arr.geometry.coordinates.length > 0) {
          arr.geometry.coordinates.forEach(element => {
            features.push({
              ...arr,
              properties: !arr.properties
                ? null
                : {
                    ...arr.properties
                  },
              geometry: {
                type: 'Point',
                coordinates: element
              }
            });
          });
        }
        continue;
      }
      if (geometryType === 'multilinestring') {
        if (arr.geometry.coordinates && arr.geometry.coordinates.length > 0) {
          arr.geometry.coordinates.forEach(element => {
            features.push({
              ...arr,
              properties: !arr.properties
                ? null
                : {
                    ...arr.properties
                  },
              geometry: {
                type: 'LineString',
                coordinates: element
              }
            });
          });
        }
        continue;
      }
      if (geometryType === 'multipolygon') {
        if (arr.geometry.coordinates && arr.geometry.coordinates.length > 0) {
          arr.geometry.coordinates.forEach(element => {
            features.push({
              ...arr,
              properties: !arr.properties
                ? null
                : {
                    ...arr.properties
                  },
              geometry: {
                type: 'Polygon',
                coordinates: element
              }
            });
          });
        }
        continue;
      }
      // extract a feature
      if (featureType === 'feature') {
        features.push({
          ...arr,
          id: `${currentFeatureEditId}`,
          properties:
            !arr.id && !arr.properties
              ? null
              : {
                  ...arr.properties,
                  id_before_edit: arr.properties?.id
                },
          geometry: arr.geometry
        } as GeoJSON.Feature);
        currentFeatureEditId++;
        continue;
      }
      currentFeatureEditId++;
    }
    return features;
  }

  public getNormalizedGeometriesOrFeaturesFromGeoJson(geometry: any): any[] {
    const geometries = [];
    const arrayStackToFlatten = [];
    arrayStackToFlatten.push(geometry);
    // find geometries inside collections
    while (arrayStackToFlatten.length > 0) {
      // Pop geometry
      const arr = arrayStackToFlatten.pop();
      if (!arr) {
        continue;
      }
      if (!arr.type) {
        if (Array.isArray(arr) && arr.length > 0) {
          arr.forEach(a => arrayStackToFlatten.push(a));
        }

        continue;
      }
      // Explore Geomtries, extracting core
      const type = arr.type.toLowerCase();
      if (type === 'featurecollection' && arr.features && arr.features.length > 0) {
        arr.features.forEach(element => {
          arrayStackToFlatten.unshift(element);
        });
        continue;
      }
      if (type === 'featurecollection') {
        continue;
      }
      if (type === 'feature' && arr.geometry?.type === 'GeometryCollection') {
        const geoFeature = { ...arr };
        delete geoFeature.geometries;
        if (arr.geometry.geometries && arr.geometry.geometries.length > 0) {
          arr.geometry.geometries.forEach(element => {
            if (!element) {
              return;
            }
            arrayStackToFlatten.unshift({ ...geoFeature, geometry: element });
          });
        } else {
          arrayStackToFlatten.unshift({ ...geoFeature, geometry: null });
        }
        continue;
      }
      if (type === 'geometrycollection') {
        if (arr.geometries && arr.geometries.length > 0) {
          arr.geometries.forEach(element => {
            if (!element) {
              return;
            }
            arrayStackToFlatten.unshift(element);
          });
        }
        continue;
      }
      if (type === 'multipoint' && arr.coordinates && arr.coordinates.length > 0) {
        arr.coordinates.forEach(element => {
          if (!element) {
            return;
          }
          geometries.push({
            type: 'Point',
            coordinates: element
          });
        });
        continue;
      }
      if (type === 'multilinestring' && arr.coordinates && arr.coordinates.length > 0) {
        arr.coordinates.forEach(element => {
          if (!element) {
            return;
          }
          geometries.push({
            type: 'LineString',
            coordinates: element
          });
        });
        continue;
      }
      if (type === 'multipolygon' && arr.coordinates && arr.coordinates.length > 0) {
        arr.coordinates.forEach(element => {
          if (!element) {
            return;
          }
          geometries.push({
            type: 'Polygon',
            coordinates: element
          });
        });
        continue;
      }
      // only extract feature geometries when this is a feature with a geometry collection
      // this is because we need to preserve features that are circles, or have other shape meta data
      if (type === 'feature' && arr.geometry?.type === 'geometrycollection') {
        arrayStackToFlatten.unshift(arr.geometry);
        continue;
      }
      // non-(featureCollection, Feature with GeometryCollection, Multi-xxx)
      geometries.push(arr);
    }

    // Rewind any non-compliant polygon rings
    geometries.forEach(fg => {
      const g = fg?.type === 'Feature' ? fg.geometry : fg;
      if (!g) {
        return;
      }
      if (g.type === 'Polygon') {
        this.rewindRings(g.coordinates as [number, number][][], true);
      }
    });
    return geometries;
  }

  public rewindRings(rings: [number, number][][], outer: boolean = true) {
    if (rings.length === 0) return;

    this.rewindRing(rings[0], outer);
    for (let i = 1; i < rings.length; i++) {
      this.rewindRing(rings[i], !outer);
    }
  }

  public rewindRing(ring: [number, number][], outer: boolean = true) {
    let area = 0,
      err = 0;
    for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
      const k = (ring[i][0] - ring[j][0]) * (ring[j][1] + ring[i][1]);
      const m = area + k;
      err += Math.abs(area) >= Math.abs(k) ? area - m + k : k - m + area;
      area = m;
    }
    if (area + err >= 0 !== !!outer) ring.reverse();
  }

  geometryAsPointGeometry(thisGeometry: any): any {
    // Generate point clone of geometry for addition to the map
    thisGeometry = thisGeometry['geometry'] ? thisGeometry['geometry'] : thisGeometry;

    switch (thisGeometry.type.toLowerCase()) {
      case 'point':
        return Object.assign(
          {},
          {
            type: 'point',
            coordinates: thisGeometry.coordinates
          }
        );

      case 'circle':
        return Object.assign(
          {},
          {
            type: 'point',
            coordinates: thisGeometry.coordinates
          }
        );

      default:
        const bounds = this.getBoundsForGeometry(thisGeometry);
        const point = bounds.getCenter();

        return {
          type: 'point',
          coordinates: [point.lng(), point.lat()]
        };
    }
  }

  public getBoundsForGeometry(geometry: any): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();

    // Explore Geometries, extracting coordinates and bounds
    return this.distanceService.getBoundsForGeometryFeatures(this.getNormalizedGeometriesOrFeaturesFromGeoJson(geometry));
  }

  /**
   * Given a bounds, construct a map location we can zoom to
   * @param bounds a google maps bounds
   * @param layer the layer this document is in, to check for a zoom size override
   * @param zoomOverride a zoom size override
   */
  public getZoomLocationFromBounds(bounds: any, layer?: Layer, zoomOverride?: number): MapLocation {
    const foundLocation = new MapLocation();
    const geo = new BboxGeometry();
    geo.ne = new Point(bounds.getNorthEast().lat(), bounds.getNorthEast().lng());
    geo.sw = new Point(bounds.getSouthWest().lat(), bounds.getSouthWest().lng());
    geo.center = new Point(bounds.getCenter().lat(), bounds.getCenter().lng());
    foundLocation.geometry = geo;

    if (
      zoomOverride &&
      (!layer ||
        (layer && !layer?.displayOptions?.zoomToFeatureLevel) ||
        (layer && zoomOverride <= layer?.displayOptions?.zoomToFeatureLevel + 1))
    ) {
      foundLocation.zoomLevel = zoomOverride;
      return foundLocation;
    }

    // add one to the the zoom level, because the map removes one level
    foundLocation.zoomLevel = (layer?.displayOptions?.zoomToFeatureLevel || 15) + 1;
    if (foundLocation.zoomLevel > 21) {
      foundLocation.zoomLevel = 21;
    }

    return foundLocation;
  }

  /**
   * Given a GeoJson geometry, find the Google maps zoom level at which the shape will cover at least N pixels
   * @param geometry a GeoJson geometry
   * @param pixelsToCover the shape will cover at least N pixels
   */
  public findMinGoogleZoomToCoverAtLeastNPixels(normalizedGeometries: any, pixelsToCover: number = 20): number[] {
    // Explore Geometries, extracting zoom levels
    const minZoomValues = normalizedGeometries.map(element => {
      return this.distanceService.findMinGoogleZoomToCoverAtLeastNPixelsForFeature(element, pixelsToCover);
    });

    return minZoomValues;
  }

  /**
   * Given the date field from data of the proxy, calculate if the value is older than last 24 hs
   * @param date date field value to check
   */
  public IsHistoricalDate(date: string): boolean {
    const dateInMilisaconds = this.parseDateToMiliseconds(new Date(date));
    const currentDateInMilisaconds = this.parseDateToMiliseconds(new Date());
    return currentDateInMilisaconds > dateInMilisaconds + 1000 * 60 * 60 * 24;
  }

  private parseDateToMiliseconds(date: Date): number {
    const utcDate = date.toUTCString().split(',');
    const tempDate = utcDate[1].trim().split(' ');
    return new Date(tempDate[0] + ' ' + tempDate[1] + tempDate[3]).getTime();
  }

  /**
   * Given a string verify if the value contains an image extension
   * @param value url image value
   */
  public isImage(value: string): boolean {
    return /(\.jpg|\.jpeg|\.bmp|\.gif|\.png)/i.test(value);
  }

  /**
   * Given a list of fields and a value map return the type
   * @param fieldKey field ket to map
   * @param fields list of fields
   */
  public findDataType(fieldKey: string, fields: { [key: string]: SchemaField }): number {
    const findField = fields[fieldKey];
    return findField ? findField.dataType : null;
  }

  public encode(str: string): string {
    return window.btoa(encodeURIComponent(str));
  }

  public decode(str: string) {
    const decodeStr = decodeURIComponent(window.atob(str));
    try {
      return JSON.parse(decodeStr);
    } catch (e) {
      // 'Error while trying to parse to JSON'
      return null;
    }
  }

  public isColorLight(rgb: { r: number; g: number; b: number }) {
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 125 ? true : false;
  }

  public isVeryLightColor(rgb: { r: number; g: number; b: number }): boolean {
    const contrastRatio = this.contrast({ r: 255, g: 255, b: 255 }, rgb);
    return contrastRatio < 1.5;
  }

  public isVeryDarkColor(rgb: { r: number; g: number; b: number }): boolean {
    const contrastRatio = this.contrast(rgb, { r: 0, g: 0, b: 0 });
    return contrastRatio < 1.5;
  }

  /* luminance: represents the brightness of a color beetween 0 to 255 in rgb.
    Comparing two colors, the one with more luminance is brighter than the other.
  */
  public luminance(r, g, b) {
    const a = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  /* The contrast ratio shows how easy a human recognises text or image on colored background.
     Minimal recommended contrast ratio is 4.5.
     if a color has ratio below that value it will have little contrast vs the background
     ref. https://www.w3.org/TR/UNDERSTANDING-WCAG20/visual-audio-contrast-contrast.html
  */
  public contrast(rgb1, rgb2) {
    return (this.luminance(rgb1.r, rgb1.g, rgb1.b) + 0.05) / (this.luminance(rgb2.r, rgb2.g, rgb2.b) + 0.05);
  }

  public hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result && result.length > 3
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  }

  public isHistoryTrackSupported(dataset: Dataset): boolean {
    if (Boolean(dataset)) {
      const { type, subtype } = dataset;
  
      if (type === DatasetType.Assets) {
        return subtype === DatasetSubtype.Person || subtype === DatasetSubtype.Vehicle;
      }
    }
  
    return false;
  };
}
